import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Avatar,
  FormHelperText,
  IconButton,
} from '@mui/material';
import { useAppSelector } from '../../../../store';
import Iconify from '../../../../components/Iconify';
import { CakeBox, GetInitialData } from '../../../../client/services';
import { Category } from '../../../../store/category/api/interface';
import { Shape } from '../../../../store/shape/api/interface';
import { Flavor } from '../../../../store/flavor/api/interface';
import { ToppingsCategoryItem } from '../../../../store/toppingsCategoryItem/api/interface';
import { CustomCakeData } from './CustomCakeBuilder';
import { useCakePreviewImage } from './useCakePreviewImage';
import SelectedCakesList from './SelectedCakesList';
import SelectedAddonsList from './SelectedAddonsList';
import { SelectedAddon } from './AddonSelector';
import { uploadAssets } from '../../../../utils/uploadAssets';
import { getAssetURl } from '../../../../utils/url';
import { DocumentType } from '../../../../enum';

interface CategoryCakeSelectorProps {
  onCakeComplete: (cake: CustomCakeData) => void;
  editingCake?: CustomCakeData | null;
  selectedCakes?: CustomCakeData[];
  onRemoveCake?: (index: number) => void;
  onEditCake?: (index: number, cake: CustomCakeData) => void;
  onQuantityChange?: (index: number, newQty: number) => void;
  selectedAddons?: SelectedAddon[];
  onRemoveAddon?: (id: string) => void;
  onAddonQuantityChange?: (id: string, newQty: number) => void;
}

// Interface for category cakes from initial data API
interface CategoryCakeFromInitialData {
  id: string;
  labelEn: string;
  labelAr: string;
  descriptionEn: string;
  descriptionAr: string;
  colorCode: string;
  isNew: boolean;
  isMostLoved: boolean;
  isSoldOut: boolean;
  displayBackgroundColor: string;
  displayMode: string;
  position: number;
  shapeId: string;
  flavorId: string;
  toppingId: string;
  cakeUniqueId: string;
}

interface CategoryFromInitialData extends Category {
  category_cakes: CategoryCakeFromInitialData[];
}

const CategoryCakeSelector: React.FC<CategoryCakeSelectorProps> = ({
  onCakeComplete,
  editingCake,
  selectedCakes = [],
  onRemoveCake,
  onEditCake,
  onQuantityChange,
  selectedAddons = [],
  onRemoveAddon,
  onAddonQuantityChange,
}) => {
  const [categories, setCategories] = useState<CategoryFromInitialData[]>([]);
  const [allCategoryCakes, setAllCategoryCakes] = useState<(CategoryCakeFromInitialData & { categoryId: string })[]>([]);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [flavors, setFlavors] = useState<Flavor[]>([]);
  const [toppingItems, setToppingItems] = useState<ToppingsCategoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { accessToken, user } = useAppSelector((state) => state.auth);
  const userRole = user?.userRole;
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCake, setSelectedCake] = useState<(CategoryCakeFromInitialData & { categoryId: string }) | null>(null);
  const [qty, setQty] = useState<number>(1);
  const [writeOnCake, setWriteOnCake] = useState<string>('');
  const [instruction, setInstruction] = useState<string>('');
  const [cakeBoxId, setCakeBoxId] = useState<string>('');
  const [cakeBoxes, setCakeBoxes] = useState<any[]>([]);
  const [customImageFile, setCustomImageFile] = useState<File | null>(null);
  const [customImagePreview, setCustomImagePreview] = useState<string | null>(null);
  const [customImagePrice, setCustomImagePrice] = useState<number | null>(null);
  const [printImageFile, setPrintImageFile] = useState<File | null>(null);
  const [printImagePreview, setPrintImagePreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [displayLimit, setDisplayLimit] = useState<number>(4); // Show 4 cakes initially
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingPrintImage, setIsUploadingPrintImage] = useState(false);

  // Filter category cakes by selected category and search query
  const filteredCakes = allCategoryCakes.filter((cake) => {
    // Filter by category
    const matchesCategory = !selectedCategoryId || cake.categoryId === selectedCategoryId;
    
    // Filter by search query (search in English and Arabic names, and descriptions)
    const matchesSearch = !searchQuery.trim() || 
      cake.labelEn?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cake.labelAr?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cake.descriptionEn?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cake.descriptionAr?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  // Get cakes to display (paginated)
  const displayedCakes = filteredCakes.slice(0, displayLimit);
  const hasMoreCakes = filteredCakes.length > displayLimit;

  const handleShowMore = () => {
    setDisplayLimit((prev) => prev + 4);
  };

  // Load data from initial data API
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        const response = await GetInitialData.find();
        
        // Extract categories with nested category_cakes
        const categoriesData = response.categories || [];
        setCategories(categoriesData);

        // Flatten all category cakes and add categoryId back
        const flattenedCakes: (CategoryCakeFromInitialData & { categoryId: string })[] = [];
        categoriesData.forEach((category: CategoryFromInitialData) => {
          if (category.category_cakes && Array.isArray(category.category_cakes)) {
            category.category_cakes.forEach((cake) => {
              flattenedCakes.push({
                ...cake,
                categoryId: category.id, // Add categoryId back
              });
            });
          }
        });
        setAllCategoryCakes(flattenedCakes);

        // Set shapes and flavors from initial data
        setShapes(response.shapes || []);
        setFlavors(response.flavors || []);

        // Extract and flatten topping items from toppingCategories
        const toppingCategoriesData = response.toppingCategories || [];
        const flattenedToppings: ToppingsCategoryItem[] = [];
        toppingCategoriesData.forEach((category: any) => {
          if (category.topping_category_items && Array.isArray(category.topping_category_items)) {
            category.topping_category_items.forEach((item: any) => {
              flattenedToppings.push(item);
            });
          }
        });
        setToppingItems(flattenedToppings);
        const boxes = response.cakeBoxes || [];
        if (boxes.length) {
          setCakeBoxes(boxes);
        } else {
          const boxResult = await CakeBox.find({
            query: { activeOnly: true, limit: 50, skip: 0 },
          });
          setCakeBoxes(boxResult?.data ?? []);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (editingCake && editingCake.categoryCakeId) {
      const cake = allCategoryCakes.find((c) => c.id === editingCake.categoryCakeId);
      if (cake) {
        setSelectedCake(cake);
        setSelectedCategoryId(cake.categoryId);
        setQty(editingCake.qty || 1);
        setWriteOnCake(editingCake.writeOnCake || '');
        setInstruction(editingCake.instruction || '');
        setCakeBoxId(editingCake.cakeBoxId || '');
        // Set custom image if exists
        if (editingCake.customImageUrl) {
          setCustomImagePreview(editingCake.customImageUrl);
          setCustomImageFile(null);
          setCustomImagePrice(editingCake.customImagePrice || null);
        } else {
          setCustomImagePreview(null);
          setCustomImageFile(null);
          setCustomImagePrice(null);
        }

        // Set print image if exists (adds 30 QAR)
        if (editingCake.printImageUrl) {
          setPrintImagePreview(editingCake.printImageUrl);
          setPrintImageFile(null);
        } else {
          setPrintImagePreview(null);
          setPrintImageFile(null);
        }
      }
    } else if (!editingCake) {
      // Reset form when not editing
      setSelectedCake(null);
      setSelectedCategoryId('');
      setQty(1);
      setWriteOnCake('');
      setInstruction('');
      setCakeBoxId('');
      setCustomImagePreview(null);
      setCustomImageFile(null);
      setCustomImagePrice(null);
      setPrintImagePreview(null);
      setPrintImageFile(null);
      setErrors({});
    }
  }, [editingCake, allCategoryCakes]);

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setSelectedCake(null); // Reset selected cake when category changes
    setDisplayLimit(4); // Reset to show 4 cakes when category changes
    setErrors((prev) => ({ ...prev, cake: '' }));
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setSelectedCake(null); // Reset selected cake when search changes
    setDisplayLimit(4); // Reset to show 4 cakes when search changes
  };

  const handleCakeSelect = (cake: CategoryCakeFromInitialData & { categoryId: string }) => {
    setSelectedCake(cake);
    setErrors((prev) => ({ ...prev, cake: '' }));
  };

  const calculatePrice = (): number => {
    if (!selectedCake) return 0;
    
    // Find shape, flavor, and topping from initial data
    const shape = shapes.find((s) => s.id === selectedCake.shapeId);
    const flavor = flavors.find((f) => f.id === selectedCake.flavorId);
    const topping = toppingItems.find((t) => t.id === selectedCake.toppingId);
    
    if (!shape || !flavor) return 0;
    
    // Shape price (use discountedPrice if available and > 0, else use regular price)
    const shapeDiscountedPrice = parseFloat(shape.discountedPrice) || 0;
    const shapePrice = shapeDiscountedPrice > 0 ? shapeDiscountedPrice : parseFloat(shape.price) || 0;
    
    // Flavor price (pricePerKg * kg)
    const flavorPricePerKg = parseFloat(flavor.pricePerKg) || 0;
    const kg = parseFloat(shape.kg) || 0;
    const flavorPrice = flavorPricePerKg * kg;
    
    // Topping price (use discountedPrice if available and > 0, else use regular price)
    let toppingPrice = 0;
    if (topping && topping.code !== 'without') {
      const toppingDiscountedPrice = parseFloat(topping.discountedPrice) || 0;
      toppingPrice = toppingDiscountedPrice > 0 ? toppingDiscountedPrice : parseFloat(topping.price) || 0;
    }
    
    // Printing charges (30 QAR if print image is uploaded - NOT for custom image)
    const printingCharges = printImagePreview ? 30 : 0;
    
    // Custom image price (if custom image is uploaded and price is set)
    const customImagePriceValue = (customImagePreview && customImagePrice !== null) ? customImagePrice : 0;
    
    // Unit price per cake = shape + flavor + topping + printing charges (if print image uploaded) + custom image price (if custom image uploaded)
    return shapePrice + flavorPrice + toppingPrice + printingCharges + customImagePriceValue;
  };

  const handleAddCake = async () => {
    const newErrors: { [key: string]: string } = {};

    if (!selectedCake) {
      newErrors.cake = 'Please select a cake';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!selectedCake) return;

        // Find shape, flavor, and topping from initial data
        const shape = shapes.find((s) => s.id === selectedCake.shapeId);
        const flavor = flavors.find((f) => f.id === selectedCake.flavorId);
        const topping = toppingItems.find((t) => t.id === selectedCake.toppingId);

        // Upload custom image if provided (no price increase)
        let customImageUrl: string | null = null;
        if (customImageFile && accessToken) {
          setIsUploadingImage(true);
          try {
            const uploadResult: any = await uploadAssets(
              customImageFile,
              DocumentType.CakePrint,
              accessToken
            );
            if (uploadResult?.data?.keypath) {
              customImageUrl = getAssetURl(uploadResult.data.keypath, assetUrl);
            } else {
              console.error('Failed to upload custom image: No keypath in response');
              setErrors((prev) => ({ ...prev, customImage: 'Failed to upload custom image. Please try again.' }));
              setIsUploadingImage(false);
              return;
            }
          } catch (error) {
            console.error('Error uploading custom image:', error);
            setErrors((prev) => ({ ...prev, customImage: 'Failed to upload custom image. Please try again.' }));
            setIsUploadingImage(false);
            return;
          } finally {
            setIsUploadingImage(false);
          }
        } else if (customImagePreview && customImagePreview.startsWith('http')) {
          // If it's already a URL (from editing), use it as is
          customImageUrl = customImagePreview;
        }

        // Upload print image if provided (adds 30 QAR)
        let printImageUrl: string | null = null;
        if (printImageFile && accessToken) {
          setIsUploadingPrintImage(true);
          try {
            const uploadResult: any = await uploadAssets(
              printImageFile,
              DocumentType.CakePrint,
              accessToken
            );
            if (uploadResult?.data?.keypath) {
              printImageUrl = getAssetURl(uploadResult.data.keypath, assetUrl);
            } else {
              console.error('Failed to upload print image: No keypath in response');
              setErrors((prev) => ({ ...prev, printImage: 'Failed to upload print image. Please try again.' }));
              setIsUploadingPrintImage(false);
              return;
            }
          } catch (error) {
            console.error('Error uploading print image:', error);
            setErrors((prev) => ({ ...prev, printImage: 'Failed to upload print image. Please try again.' }));
            setIsUploadingPrintImage(false);
            return;
          } finally {
            setIsUploadingPrintImage(false);
          }
        } else if (printImagePreview && printImagePreview.startsWith('http')) {
          // If it's already a URL (from editing), use it as is
          printImageUrl = printImagePreview;
        }

        // Convert CategoryCake to CustomCakeData format
        const cakeData: CustomCakeData = {
          shapeId: selectedCake.shapeId,
          flavorId: selectedCake.flavorId,
          colorCode: selectedCake.colorCode,
          displayMode: (selectedCake.displayMode as 'front' | 'top') || 'front',
          toppingId: selectedCake.toppingId || null,
          printImageUrl: printImageUrl,
          customImageUrl: customImageUrl,
          customImagePrice: customImageUrl ? customImagePrice : null,
          categoryId: selectedCake.categoryId,
          categoryCakeId: selectedCake.id,
          writeOnCake: writeOnCake.trim() || null,
          instruction: instruction.trim() || null,
          cakeBoxId: cakeBoxId || null,
          cakeBoxLabelEn: cakeBoxes.find((box) => box.id === cakeBoxId)?.labelEn || null,
          cakeBoxPrice: cakeBoxId
            ? Number(cakeBoxes.find((box) => box.id === cakeBoxId)?.price || 0)
            : null,
          qty,
          shapeLabelEn: shape?.labelEn || '',
          shapeLabelAr: shape?.labelAr || '',
          flavorLabelEn: flavor?.labelEn || '',
          flavorLabelAr: flavor?.labelAr || '',
          toppingLabelEn: topping ? (topping.code === 'without' ? 'Without' : topping.labelEn) : null,
          toppingLabelAr: topping ? (topping.code === 'without' ? 'بدون' : topping.labelAr) : null,
          unitPrice: calculatePrice(),
        };

    onCakeComplete(cakeData);

    // Reset form only if not editing
    if (!editingCake) {
      setSelectedCake(null);
      setSelectedCategoryId('');
      setQty(1);
      setWriteOnCake('');
      setInstruction('');
      setCakeBoxId('');
      setCustomImagePreview(null);
      setCustomImageFile(null);
      setCustomImagePrice(null);
      setPrintImagePreview(null);
      setPrintImageFile(null);
      setErrors({});
    }
  };

  if (isLoading) {
    return (
      <Card sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant='h6' gutterBottom>
        Select from Category Cakes
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Left Side - Category Selection and Cake Grid */}
        <Grid item xs={12} md={8}>
          {/* Category Selection */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Category</InputLabel>
            <Select
              value={selectedCategoryId}
              onChange={(e) => handleCategoryChange(e.target.value)}
              label='Select Category'
            >
              <MenuItem value=''>All Categories</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.labelEn} ({category.labelAr})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder='Search cakes by name or description...'
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                  <Iconify icon='eva:search-fill' sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                </Box>
              ),
              endAdornment: searchQuery && (
                <IconButton
                  size='small'
                  onClick={() => handleSearchChange('')}
                  sx={{ mr: -1 }}
                >
                  <Iconify icon='eva:close-fill' sx={{ width: 20, height: 20 }} />
                </IconButton>
              ),
            }}
          />

          {/* Category Cakes Grid */}
          {filteredCakes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant='body2' color='text.secondary'>
                {searchQuery
                  ? `No cakes found matching "${searchQuery}"${selectedCategoryId ? ' in this category' : ''}`
                  : selectedCategoryId
                  ? 'No cakes found in this category'
                  : 'No category cakes available'}
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                {displayedCakes.map((cake) => (
                  <Grid item xs={12} sm={6} key={cake.id}>
                    <Card
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: selectedCake?.id === cake.id ? 2 : 1,
                        borderColor:
                          selectedCake?.id === cake.id ? '#EE3750' : '#e0e0e0',
                        '&:hover': {
                          borderColor: '#EE3750',
                          boxShadow: 2,
                        },
                      }}
                      onClick={() => handleCakeSelect(cake)}
                    >
                      <Box sx={{ mb: 2, textAlign: 'center', minHeight: 150 }}>
                        <CategoryCakePreview cake={cake} toppingItems={toppingItems} />
                      </Box>
                      <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
                        {cake.labelEn}
                      </Typography>
                      <Typography variant='body2' color='text.secondary' gutterBottom>
                        {cake.labelAr}
                      </Typography>
                      {cake.descriptionEn && (
                        <Typography variant='caption' color='text.secondary' display='block'>
                          {cake.descriptionEn}
                        </Typography>
                      )}
                      <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {cake.isNew && (
                          <Chip label='New' size='small' color='primary' />
                        )}
                        {cake.isMostLoved && (
                          <Chip label='Most Loved' size='small' color='secondary' />
                        )}
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              {/* Show More Button */}
              {hasMoreCakes && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button
                    variant='outlined'
                    onClick={handleShowMore}
                    sx={{
                      borderColor: '#EE3750',
                      color: '#EE3750',
                      '&:hover': {
                        borderColor: '#EE3750',
                        backgroundColor: '#FDF0F1',
                      },
                    }}
                  >
                    Show More ({filteredCakes.length - displayLimit} more)
                  </Button>
                </Box>
              )}
            </>
          )}
        </Grid>

        {/* Right Side - Selected Cake Details */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              position: 'sticky',
              top: 20,
            }}
          >
            {/* Selected Cakes List at the top */}
            {selectedCakes.length > 0 && onRemoveCake && onEditCake && onQuantityChange && (
              <Box sx={{ mb: 3 }}>
                <SelectedCakesList
                  cakes={selectedCakes}
                  onRemove={onRemoveCake}
                  onEdit={onEditCake}
                  onQuantityChange={onQuantityChange}
                />
              </Box>
            )}

            {/* Selected Addons below Selected Cakes */}
            {selectedAddons.length > 0 && onRemoveAddon && onAddonQuantityChange && (
              <Box sx={{ mb: 3 }}>
                <SelectedAddonsList
                  addons={selectedAddons}
                  onRemove={onRemoveAddon}
                  onQuantityChange={onAddonQuantityChange}
                />
              </Box>
            )}

            {selectedCake ? (
              <>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#FDF0F1',
                    borderRadius: 1,
                    border: '1px solid #EE3750',
                    mb: 2,
                  }}
                >
                  <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
                    Selected Cake: {selectedCake.labelEn}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {selectedCake.labelAr}
                  </Typography>
                  {selectedCake.descriptionEn && (
                    <Typography variant='body2' sx={{ mt: 1 }}>
                      {selectedCake.descriptionEn}
                    </Typography>
                  )}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant='body2' color='text.secondary' display='inline'>
                      Shape:{' '}
                    </Typography>
                    <Typography variant='body2' display='inline'>
                      {shapes.find((s) => s.id === selectedCake.shapeId)?.labelEn || 'N/A'} (
                      {shapes.find((s) => s.id === selectedCake.shapeId)?.labelAr || 'N/A'})
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant='body2' color='text.secondary' display='inline'>
                      Flavor:{' '}
                    </Typography>
                    <Typography variant='body2' display='inline'>
                      {flavors.find((f) => f.id === selectedCake.flavorId)?.labelEn || 'N/A'} (
                      {flavors.find((f) => f.id === selectedCake.flavorId)?.labelAr || 'N/A'})
                    </Typography>
                  </Box>
                  {selectedCake.toppingId && toppingItems.find((t) => t.id === selectedCake.toppingId) && (
                    <Box>
                      <Typography variant='body2' color='text.secondary' display='inline'>
                        Topping:{' '}
                      </Typography>
                      <Typography variant='body2' display='inline'>
                        {toppingItems.find((t) => t.id === selectedCake.toppingId)?.labelEn || 'N/A'} (
                        {toppingItems.find((t) => t.id === selectedCake.toppingId)?.labelAr || 'N/A'})
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Write on Cake */}
                <TextField
                  fullWidth
                  label='Write on Cake (Optional)'
                  value={writeOnCake}
                  onChange={(e) => setWriteOnCake(e.target.value)}
                  placeholder='e.g., Happy Birthday'
                  sx={{ mb: 2 }}
                />

                {/* Instructions */}
                <TextField
                  fullWidth
                  label='Instructions (Optional)'
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  placeholder='Special instructions for the cake'
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Premium Packaging (Optional)</InputLabel>
                  <Select
                    value={cakeBoxId}
                    label='Premium Packaging (Optional)'
                    onChange={(e) => setCakeBoxId(String(e.target.value))}
                  >
                    <MenuItem value=''>None</MenuItem>
                    {cakeBoxes.map((box) => (
                      <MenuItem key={box.id} value={box.id}>
                        {box.labelEn} (+ {Number(box.price || 0).toFixed(2)} QAR)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Custom Image Upload */}
                <Box sx={{ border: '1px dashed #ccc', borderRadius: 1, p: 2, mb: 2 }}>
                  <Typography variant='subtitle2' gutterBottom>
                    Custom Image (Optional)
                  </Typography>
                  <input
                    accept='image/*'
                    style={{ display: 'none' }}
                    id='category-cake-custom-image-upload'
                    type='file'
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setCustomImageFile(file);
                        // Create preview
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setCustomImagePreview(reader.result as string);
                        };
                        reader.readAsDataURL(file);
                        setErrors((prev) => ({ ...prev, customImage: '' }));
                      }
                    }}
                  />
                  <label htmlFor='category-cake-custom-image-upload'>
                    <Button
                      variant='outlined'
                      component='span'
                      sx={{
                        mb: customImagePreview ? 2 : 0,
                        borderColor: '#EE3750',
                        color: '#EE3750',
                        '&:hover': {
                          borderColor: '#EE3750',
                          backgroundColor: '#FDF0F1',
                        },
                      }}
                    >
                      Choose Image File
                    </Button>
                  </label>
                  {customImagePreview && (
                    <Box sx={{ mt: 2 }}>
                      <img
                        src={customImagePreview}
                        alt='Custom image preview'
                        style={{
                          maxWidth: '100%',
                          maxHeight: '150px',
                          objectFit: 'contain',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                        }}
                      />
                      <Button
                        size='small'
                        onClick={() => {
                          setCustomImagePreview(null);
                          setCustomImageFile(null);
                          setCustomImagePrice(null);
                        }}
                        sx={{ mt: 1, color: '#d32f2f' }}
                      >
                        Remove Image
                      </Button>
                    </Box>
                  )}
                  {/* Custom Image Price Field (Admin Only) */}
                  {customImagePreview && (userRole === 'admin' || userRole === 'orderManager') && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        fullWidth
                        type='number'
                        label='Custom Image Price (QAR)'
                        value={customImagePrice !== null ? customImagePrice : ''}
                        onChange={(e) => {
                          const value = e.target.value === '' ? null : parseFloat(e.target.value);
                          setCustomImagePrice(value !== null && !isNaN(value) && value >= 0 ? value : null);
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                        helperText='Enter price for custom image (will be added to cake price)'
                      />
                    </Box>
                  )}
                  {errors.customImage && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {errors.customImage}
                    </FormHelperText>
                  )}
                </Box>

                {/* Print Image Upload (adds 30 QAR) */}
                <Box sx={{ border: '1px dashed #ccc', borderRadius: 1, p: 2, mb: 2 }}>
                  <Typography variant='subtitle2' gutterBottom>
                    Print Image on Cake (Optional)
                  </Typography>
                  <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
                    This image will be printed on the cake (+30 QAR)
                  </Typography>
                  <input
                    accept='image/*'
                    style={{ display: 'none' }}
                    id='category-cake-print-image-upload'
                    type='file'
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setPrintImageFile(file);
                        // Create preview
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setPrintImagePreview(reader.result as string);
                        };
                        reader.readAsDataURL(file);
                        setErrors((prev) => ({ ...prev, printImage: '' }));
                      }
                    }}
                  />
                  <label htmlFor='category-cake-print-image-upload'>
                    <Button
                      variant='outlined'
                      component='span'
                      disabled={isUploadingPrintImage}
                      sx={{
                        mb: printImagePreview ? 2 : 0,
                        borderColor: '#EE3750',
                        color: '#EE3750',
                        '&:hover': {
                          borderColor: '#EE3750',
                          backgroundColor: '#FDF0F1',
                        },
                      }}
                    >
                      {isUploadingPrintImage ? 'Uploading...' : 'Choose Image File'}
                    </Button>
                  </label>
                  {printImagePreview && (
                    <Box sx={{ mt: 2 }}>
                      <img
                        src={printImagePreview}
                        alt='Print image preview'
                        style={{
                          maxWidth: '100%',
                          maxHeight: '150px',
                          objectFit: 'contain',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                        }}
                      />
                      <Button
                        size='small'
                        onClick={() => {
                          setPrintImagePreview(null);
                          setPrintImageFile(null);
                        }}
                        sx={{ mt: 1, color: '#d32f2f' }}
                      >
                        Remove Image
                      </Button>
                    </Box>
                  )}
                  {errors.printImage && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {errors.printImage}
                    </FormHelperText>
                  )}
                </Box>

                {/* Quantity */}
                <TextField
                  fullWidth
                  type='number'
                  label='Quantity'
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                  inputProps={{ min: 1 }}
                  sx={{ mb: 2 }}
                />

                {/* Price Display */}
                <Box sx={{ p: 2, backgroundColor: '#FDF0F1', borderRadius: 1, mb: 2 }}>
                  <Typography variant='body2' color='text.secondary'>
                    Unit Price:
                  </Typography>
                  <Typography variant='h6'>{calculatePrice().toFixed(2)} QAR</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Total: {(calculatePrice() * qty).toFixed(2)} QAR
                  </Typography>
                </Box>

                 {/* Error Messages */}
                 {errors.cake && (
                   <FormHelperText error sx={{ mb: 2 }}>
                     {errors.cake}
                   </FormHelperText>
                 )}
                 {errors.printImage && (
                   <FormHelperText error sx={{ mb: 2 }}>
                     {errors.printImage}
                   </FormHelperText>
                 )}

                 {/* Add Cake Button */}
                 <Button
                   fullWidth
                   variant='contained'
                   onClick={handleAddCake}
                   disabled={isUploadingImage || isUploadingPrintImage}
                   sx={{
                     backgroundColor: '#EE3750 !important',
                     '&:hover': {
                       backgroundColor: '#EE3750 !important',
                     },
                     '&:disabled': {
                       backgroundColor: '#c4cdd5',
                       color: '#fff',
                     },
                   }}
                 >
                   {isUploadingImage ? (
                     <>
                       <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                       Uploading Image...
                     </>
                   ) : (
                     editingCake ? 'Update Cake in Order' : 'Add Cake to Order'
                   )}
                 </Button>
              </>
            ) : (
              <Box
                sx={{
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: '#FDF0F1',
                  borderRadius: 1,
                  border: '1px dashed #EE3750',
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  Select a cake to see details and add to order
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

// Preview component for category cake
interface CategoryCakePreviewProps {
  cake: CategoryCakeFromInitialData & { categoryId: string };
  toppingItems: ToppingsCategoryItem[]; // Pass toppingItems from parent instead of using Redux
}

const CategoryCakePreview: React.FC<CategoryCakePreviewProps> = ({ cake, toppingItems }) => {
  const { data: shapes } = useAppSelector((state) => state.shape);
  const { data: flavors } = useAppSelector((state) => state.flavor);

  const shape = useMemo(() => shapes.find((s) => s.id === cake.shapeId), [shapes, cake.shapeId]);
  const flavor = useMemo(() => flavors.find((f) => f.id === cake.flavorId), [flavors, cake.flavorId]);

  // Memoize the mock cake object to prevent unnecessary re-renders
  const mockCake = useMemo(() => {
    if (!shape || !flavor) return null;
    return {
      shapeId: cake.shapeId,
      flavorId: cake.flavorId,
      colorCode: cake.colorCode,
      displayMode: (cake.displayMode as 'front' | 'top') || 'front',
      toppingId: cake.toppingId || null,
    };
  }, [shape, flavor, cake.shapeId, cake.flavorId, cake.colorCode, cake.displayMode, cake.toppingId]);

  // Memoize arrays to prevent unnecessary effect re-runs
  // Pass all toppingItems (not just the found one) so useCakePreviewImage can find the topping
  const shapesArray = useMemo(() => shape ? [shape] : [], [shape]);
  const flavorsArray = useMemo(() => flavor ? [flavor] : [], [flavor]);
  const toppingItemsArray = useMemo(() => toppingItems || [], [toppingItems]);

  const { previewImage, isLoading } = useCakePreviewImage({
    cake: mockCake as any,
    shapes: shapesArray,
    flavors: flavorsArray,
    toppingItems: toppingItemsArray,
  });

  if (isLoading) {
    return <CircularProgress size={40} />;
  }

  if (previewImage) {
    return (
      <Avatar
        imgProps={{ crossOrigin: 'anonymous' }}
        src={previewImage}
        sx={{
          width: '100%',
          height: 150,
          borderRadius: 'unset',
          objectFit: 'contain',
          '& img': { objectFit: 'contain' },
          border: '1px solid #ddd',
          bgcolor: 'background.paper',
        }}
        variant='square'
      />
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: 150,
        backgroundColor: cake.displayBackgroundColor || '#FDF0F1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 1,
        border: '1px solid #ddd',
      }}
    >
      <Typography variant='body2' color='text.secondary'>
        {cake.labelEn}
      </Typography>
    </Box>
  );
};

export default CategoryCakeSelector;

